<template>
  <Teleport to="body">
    <Transition name="cart-interstitial" @after-leave="onAfterLeave">
      <div
        v-if="item"
        data-ht-module="cart-interstitial"
        class="CartInterstitial"
        role="dialog"
        aria-modal="true"
        :aria-label="headline"
      >
        <div class="CartInterstitial__Backdrop" @click="close" />
        <div ref="modal" class="CartInterstitial__Modal">
          <div
            class="CartInterstitial__Scroll"
            :style="modalHeight ? { height: `${modalHeight}px` } : null"
          >
            <div ref="content" class="CartInterstitial__Content">
              <button
                class="CartInterstitial__Close"
                data-ht-element="cart-interstitial-close"
                :aria-label="closeLabel"
                @click="close"
              >
                <Icon icon="close" :aria-hidden="true" />
              </button>
              <p class="h4 CartInterstitial__Headline">
                <Icon icon="check--goldenhour" :aria-hidden="true" />{{
                  headline
                }}
              </p>
              <div class="CartInterstitial__Item">
                <LineItemMedia v-if="!item.is_bundle" :item="item" />
                <div class="CartInterstitial__Details">
                  <div class="CartInterstitial__TitleRow">
                    <p class="t4 CartInterstitial__Title">{{ item.title }}</p>
                    <button
                      v-if="item.is_bundle && hasComponents"
                      type="button"
                      class="CartInterstitial__Caret"
                      :class="{
                        'CartInterstitial__Caret--Open': accordionOpen,
                      }"
                      :aria-expanded="accordionOpen"
                      aria-controls="cart-interstitial-components"
                      :aria-label="accordionToggleLabel"
                      @click="accordionOpen = !accordionOpen"
                    >
                      <Icon icon="chevron-down" :aria-hidden="true" />
                    </button>
                  </div>
                  <LineItemOptions :options="item.options" />
                  <Price
                    :item="item"
                    :ready="true"
                    :variant="item"
                    location="cart"
                    :cart="store.items"
                    :components="item?.components"
                  />
                </div>
              </div>
              <ul
                v-if="hasComponents"
                id="cart-interstitial-components"
                class="CartInterstitial__Components"
                :class="{
                  'CartInterstitial__Components--Open': accordionOpen,
                }"
                :aria-hidden="!accordionOpen"
              >
                <li
                  v-for="component in item.components"
                  :key="component"
                  class="CartInterstitial__Component"
                >
                  <LineItemMedia :item="component" />
                  <div class="CartInterstitial__Details">
                    <p class="t4 CartInterstitial__Title">
                      {{ component.title }}
                    </p>
                    <LineItemOptions :options="component.options" />
                    <Price
                      :item="component"
                      :is-component="true"
                      :ready="true"
                      :variant="item"
                      location="cart"
                      :cart="store.items"
                    />
                  </div>
                </li>
              </ul>
            </div>
          </div>
          <div class="CartInterstitial__Actions">
            <a
              href="/cart"
              class="button button--primary"
              data-ht-element="cart-interstitial-view-cart-cta"
              >{{ viewCartLabel }}</a
            >
            <button
              type="button"
              class="button button--secondary"
              data-ht-element="cart-interstitial-continue-shopping-cta"
              @click="close"
            >
              {{ continueShoppingLabel }}
            </button>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup>
import { computed, ref, watch, nextTick, onBeforeUnmount } from 'vue';
import { useCartStore } from './Composables/Store';
import { trapFocus, releaseFocus } from '@utils/global-functions';
import LineItemMedia from './LineItemMedia.vue';
import LineItemOptions from './LineItemOptions.vue';
import Price from '../Global/Price.vue';
import Icon from '../Global/Icon.vue';

const store = useCartStore();
const modal = ref(null);
const content = ref(null);
const accordionOpen = ref(false);
const modalHeight = ref(null);
let resizeObserver = null;

const item = computed(() => store.interstitialItem);
const hasComponents = computed(() => item.value?.components?.length > 0);
const accordionToggleLabel = computed(() =>
  accordionOpen.value ? 'Hide bundle contents' : 'Show bundle contents'
);

const headline = computed(
  () => store.terms?.interstitial?.headline ?? 'Item Added to Cart'
);
const viewCartLabel = computed(
  () => store.terms?.interstitial?.view_cart ?? 'View Cart'
);
const continueShoppingLabel = computed(
  () => store.terms?.interstitial?.continue_shopping ?? 'Continue Shopping'
);
const closeLabel = computed(() => `Close ${headline.value}`);

const close = () => {
  store.closeInterstitial();
  releaseFocus();
};

const onKeydown = (e) => {
  if (e.key === 'Escape') close();
};

let lockedScrollY = 0;

/**
 * Locks the page in its current scroll position while the interstitial is open.
 * `overflow: hidden` alone still lets iOS Safari rubber-band the background, so
 * the body is pinned via `position: fixed` at its current offset instead.
 */
const lockScroll = () => {
  lockedScrollY = window.scrollY;
  document.body.style.position = 'fixed';
  document.body.style.top = `-${lockedScrollY}px`;
  document.body.style.left = '0';
  document.body.style.right = '0';
  document.body.style.width = '100%';
};

const unlockScroll = () => {
  // While position: fixed, the document has no scrollable height, so scrollTo
  // must happen *after* un-fixing the body — otherwise there's nowhere yet to
  // scroll to and it's silently a no-op, leaving the page stuck at the top.
  document.body.style.position = '';
  document.body.style.top = '';
  document.body.style.left = '';
  document.body.style.right = '';
  document.body.style.width = '';

  // The site sets `scroll-behavior: smooth` globally, which would otherwise
  // animate this restore as a visible glide up from the top — force an
  // instant jump instead, then restore the site's normal smooth scrolling.
  const html = document.documentElement;
  const previousScrollBehavior = html.style.scrollBehavior;
  html.style.scrollBehavior = 'auto';
  window.scrollTo(0, lockedScrollY);
  html.style.scrollBehavior = previousScrollBehavior;
};

/**
 * Keeps modalHeight in sync with the content's natural height, so the modal
 * can transition height smoothly (e.g. toggling the bundle accordion) instead
 * of jumping — a plain CSS transition can't animate to/from `auto`.
 */
const observeContentHeight = () => {
  if (!content.value) return;
  resizeObserver = new ResizeObserver((entries) => {
    modalHeight.value = entries[0].target.offsetHeight;
  });
  resizeObserver.observe(content.value);
};

const stopObservingContentHeight = () => {
  resizeObserver?.disconnect();
  resizeObserver = null;
  modalHeight.value = null;
};

/**
 * Runs the close-side cleanup that was previously done synchronously in the
 * `watch(item, ...)` callback below the moment `item` went falsy. `watch`'s
 * default `pre` flush timing runs *before* Vue patches the DOM, so doing this
 * there disrupted the leave transition's starting box (unlocking the body's
 * scroll position forces a synchronous reflow, and nulling `modalHeight`
 * changes layout) before `<Transition>` ever got to animate it — the mobile
 * slide-down collapsed to a no-op. Running it in `@after-leave` instead lets
 * the 300ms leave transition play out undisturbed first.
 */
const onAfterLeave = () => {
  document.removeEventListener('keydown', onKeydown);
  stopObservingContentHeight();
  unlockScroll();
};

watch(item, async (open) => {
  if (!open) return;
  accordionOpen.value = false;
  lockScroll();
  document.addEventListener('keydown', onKeydown);
  await nextTick();
  if (modal.value) trapFocus(modal.value);
  observeContentHeight();
});

onBeforeUnmount(() => {
  stopObservingContentHeight();
  unlockScroll();
});
</script>
